import BenchlingLinkRequiredToast from '@/components/Account/BenchlingAuth/BenchlingLinkRequiredToast/BenchlingLinkRequiredToast';
import type { ActionIcon } from '@/components/UI/SelectDropDown/Menus/ActionList';
import type { components } from '@/generated/internal-api/openapi-types';
import { errorToast } from '@/helpers';
import { _notNil } from '@/littledash';
import { ApiService } from '@/support/ApiService';
import { useSyncExternalStore } from 'react';

export type BenchlingLinkStatus = components['schemas']['BenchlingAuthStatusV1.schema']['status'];

export interface BenchlingLinkState {
  status: BenchlingLinkStatus;
  /** Set only while LINKED, and even then null when Benchling did not say. */
  refreshTokenExpiresAt: string | null;
}

export const NOT_LINKED: BenchlingLinkState = { status: 'NOT_LINKED', refreshTokenExpiresAt: null };

/**
 * The acting user's Benchling link on the current team, shared by every component that gates on it.
 *
 * Static rather than component state because Authenticated, which loads it, remounts on some route
 * changes without loading again. Authenticated clears it whenever it starts loading a team; logout
 * reloads the page, which clears it too.
 */
export class BenchlingLinkService {
  static #state: BenchlingLinkState = NOT_LINKED;
  static #version = 0;
  static #eventEmitter = new EventTarget();

  static get(): BenchlingLinkState {
    return BenchlingLinkService.#state;
  }

  /** Advances on every change, so a read that began earlier can tell it has been overtaken. */
  static version(): number {
    return BenchlingLinkService.#version;
  }

  static set(next: BenchlingLinkState): void {
    if (next === BenchlingLinkService.#state) {
      return;
    }
    BenchlingLinkService.#state = next;
    BenchlingLinkService.#version += 1;
    BenchlingLinkService.#eventEmitter.dispatchEvent(new Event('benchling-link:change'));
  }

  /** Applies a read's result only if nothing has changed the link since the read began. */
  static setIfUnchangedSince(version: number, next: BenchlingLinkState): boolean {
    if (version !== BenchlingLinkService.#version) {
      return false;
    }
    BenchlingLinkService.set(next);
    return true;
  }

  static subscribe(listener: () => void): () => void {
    BenchlingLinkService.#eventEmitter.addEventListener('benchling-link:change', listener);
    return () => BenchlingLinkService.#eventEmitter.removeEventListener('benchling-link:change', listener);
  }

  /** Static state outlives a test; call between tests that touch it. */
  static reset(): void {
    BenchlingLinkService.#state = NOT_LINKED;
    BenchlingLinkService.#version = 0;
    BenchlingLinkService.#eventEmitter = new EventTarget();
  }
}

/** Reads the link state and re-renders when anything learns it has changed. */
export const useBenchlingLink = (): BenchlingLinkState =>
  useSyncExternalStore(BenchlingLinkService.subscribe, BenchlingLinkService.get);

type BenchlingDelegatedError = components['schemas']['BenchlingDelegatedErrorV1.schema'];
export type BenchlingDelegatedFailureReason = BenchlingDelegatedError['reason'];

/** The failure a delegated endpoint reported. */
export interface BenchlingDelegatedFailure {
  reason: BenchlingDelegatedFailureReason;
  retryable: boolean;
}

/**
 * What a failed startup check falls back to. Failing open keeps delegated actions as they were before
 * the check existed: the server still refuses an unlinked user, and that refusal corrects this state.
 * Failing closed would disable every linked user's actions, with a tooltip telling them to link.
 */
const UNCHECKED: BenchlingLinkState = { status: 'LINKED', refreshTokenExpiresAt: null };

/**
 * Asks the server for the link state, rotating an ageing refresh token on the way.
 *
 * Safe before the team's feature flags are known: a team without delegated auth answers NOT_LINKED.
 * Never throws, so it can sit in a startup batch without failing it.
 */
export const fetchBenchlingLinkState = async (): Promise<BenchlingLinkState> => {
  const response = await ApiService.call({
    endpoint: 'POST /api/v1/benchling-auth/delegation-health-check',
    options: { onError: { throw: false, toast: false, capture: true, slug: 'benchling-auth-delegation-health-check' } },
  });
  if (response.type !== 'success') {
    return UNCHECKED;
  }
  return { status: response.body.status, refreshTokenExpiresAt: response.body.refresh_token_expires_at ?? null };
};

/**
 * Re-reads the stored link without touching Benchling, or undefined if the read failed.
 *
 * For learning of a change made elsewhere, such as another tab completing consent; unlike the health
 * check it never rotates tokens.
 */
export const fetchBenchlingLinkStatus = async (): Promise<BenchlingLinkState | undefined> => {
  const response = await ApiService.call({
    endpoint: 'GET /api/v1/benchling-auth/status',
    options: { onError: { throw: false, toast: false, capture: true, slug: 'benchling-auth-status' } },
  });
  if (response.type !== 'success') {
    return undefined;
  }
  return linkStateOf(response.body.status, response.body.refresh_token_expires_at);
};

/** A state for a status learned without an expiry, e.g. from the account screen's own read. */
export const linkStateOf = (
  status: BenchlingLinkStatus,
  refreshTokenExpiresAt?: string | null
): BenchlingLinkState => ({
  status,
  refreshTokenExpiresAt: status === 'LINKED' ? (refreshTokenExpiresAt ?? null) : null,
});

/**
 * Pulls a delegated endpoint's failure classification out of an axios-shaped error.
 *
 * `retryable` comes from the server rather than being inferred from the reason, so the two cannot
 * drift: the server owns which failures are worth another attempt.
 */
export const benchlingDelegatedFailure = (error: unknown): BenchlingDelegatedFailure | undefined => {
  const data = (error as { response?: { data?: Partial<BenchlingDelegatedError> } })?.response?.data;
  if (!_notNil(data?.reason)) {
    return undefined;
  }
  return { reason: data.reason, retryable: data.retryable ?? false };
};

/**
 * What to tell the user for each failure, worded around what they can do about it.
 *
 * `actionLabel` completes "permission to …", e.g. "create this worklist". A permission problem and a
 * rate limit need different words, because inviting a retry on the former sends the user nowhere.
 */
export const benchlingDelegatedFailureMessage = (failure: BenchlingDelegatedFailure, actionLabel: string): string => {
  switch (failure.reason) {
    case 'benchling_link_required':
      return `Link your Benchling account from your account page to ${actionLabel}.`;
    case 'benchling_permission_denied':
      return `Your Benchling account does not have permission to ${actionLabel}.`;
    case 'benchling_rate_limited':
      return 'Benchling is rate limiting requests. Please try again in a moment.';
    case 'benchling_unavailable':
      return 'Benchling could not be reached. Please try again shortly.';
    case 'benchling_request_rejected':
    default:
      return `Benchling rejected the request to ${actionLabel}. Please contact support.`;
  }
};

/** Explains the Benchling mark wherever it flags an action that runs in Benchling as the user. */
export const BENCHLING_DELEGATED_TOOLTIP = 'This is a Benchling core action, attributed to your linked user account.';

/**
 * Marks a menu action that acts in Benchling as the signed-in user. Shared so every delegated action
 * in a menu carries the same mark.
 */
export const BENCHLING_DELEGATED_ACTION_ICON: ActionIcon = {
  icon: 'jeffy_p_circle',
  tooltip: BENCHLING_DELEGATED_TOOLTIP,
};

/**
 * Why a delegated action is unavailable, or undefined when it is available.
 *
 * Shared by every gated control so a lapsed link and a missing one read the same wherever they stop
 * the user.
 */
export const benchlingLinkDisabledReason = (status: BenchlingLinkStatus, actionLabel: string): string | undefined => {
  switch (status) {
    case 'LINKED':
      return undefined;
    case 'LINK_EXPIRED':
      return `Your Benchling link has expired. Reconnect it from your account page to ${actionLabel}.`;
    case 'NOT_LINKED':
    default:
      return `Link your Benchling account from your account page to ${actionLabel}.`;
  }
};

/**
 * Raised from every place that learns the link is unusable: the startup check and any delegated call.
 *
 * One toast id across all of them, so a check and an action reporting the same lapse cannot stack two.
 */
export const showBenchlingLinkRequiredToast = (expired: boolean): void => {
  errorToast(({ closeToast }) => <BenchlingLinkRequiredToast expired={expired} onDismiss={closeToast} />, {
    toastId: 'benchling-link-required',
    closeOnClick: false,
    autoClose: 30_000,
  });
};
